<template>
  <div class="kpt-accounts-selector">
    <b-taglist>
      <b-tag v-for="(account, idx) in checkedAccounts"
        :key="idx"
        type="is-primary"
        closable
        closeType='is-danger'
        aria-close-label="Close"
        @close="deselectAccount(account)">
        {{ account.name }}
      </b-tag>
    </b-taglist>
    <b-input type="text" icon="search" @input="getFilteredAccounts" v-model="searchTerm" :loading="isFetchingAccounts" placeholder="Search by Account name..."></b-input>
    <b-table 
      :data="availableAccounts" 
      :scrollable="false"
      checkable 
      :header-checkable="false"
      :is-row-checkable="(row) => canSelect && isRowCheckable(row)"
      :custom-is-checked="(a, b) => a.id === b.id"
      :checked-rows.sync="checkedAccounts"
      :paginated="true"
      :per-page="5"
      :pagination-simple="true"
      @check="emitUpdate"
      :current-page="currentPage">
      <b-table-column field="name" label="Account Name" v-slot="props" sortable>
        {{ props.row.name }}
      </b-table-column>
      <template slot="empty">
        <p v-if="!isFetchingAccounts">No available Accounts found.</p>
        <p v-else>Loading accounts...</p>
      </template>
    </b-table>
  </div>
</template>
<script>
import { debounce, orderBy, uniqBy } from 'lodash';

export default {
  data() {
    return {
      isFirstLoad: true,
      searchTerm: null,
      availableAccounts: [],
      checkedAccounts: [],
      isFetchingAccounts: false,
      currentPage: 1
    }
  },
  props: {
    excludeAccounts: {
      type: Array,
      default: () => []
    },
    canSelect: {
      type: Boolean,
      default: true
    },
    preSelected: {
      type: Array,
      default: () => []
    }
  },
  mounted() {
    this.getFilteredAccounts();
  },
  watch: {
    // eslint-disable-next-line func-names, quote-props, no-unused-vars
    'canSelect': function (newValue, oldValue) {
      if (!newValue) {
        this.checkedAccounts = [];
        this.emitUpdate([]);
      }
    }
  },
  computed: {
    accounts() { 
      return orderBy(this.$store.state.accounts.accounts, ['name'], ['asc']);
    },
    sharedAccounts() {
      return orderBy(this.$store.state.accounts.sharedAccounts, ['name'], ['asc']);
    },
    allAccounts() {
      // join accounts and sharedAccounts arrays
      const concatAccounts = this.accounts.concat(this.sharedAccounts);
      // return unique to prevent duplicates
      return uniqBy(concatAccounts, 'id');
    }
  },
  methods: {
    refreshAccounts() {
      this.getFilteredAccounts();
    },
    // eslint-disable-next-line func-names
    getFilteredAccounts: debounce(async function (searchTerm) {
      this.isFetchingAccounts = true;

      // snag a clone of all accounts
      let accounts = [...this.allAccounts];
      // filter by searchTerm if present
      if (searchTerm) accounts = accounts.filter(a => a.name.toLowerCase().includes(searchTerm.toLowerCase()));
      // check for exclusions to remove specified accounts from selection
      if (this.excludeAccounts.length) accounts = accounts.filter(a => !this.excludeAccounts.includes(a.id)); 
      // simplify the objects
      accounts = accounts.map(a => ({ id: a.id, name: a.name }));
      // order the accounts for the table
      this.availableAccounts = orderBy(accounts, ['name'], ['asc']);

      // check for pre-selected items passed to the component
      if (this.isFirstLoad && this.preSelected && !!this.preSelected.length) {
        const preSelectedIds = this.preSelected.map(ps => ps.id);
        const checked = this.availableAccounts.filter(as => preSelectedIds.includes(as.id));
        this.checkedAccounts = checked;
      }
      // prevent pre-selected items from impacting selection for future searches
      this.isFirstLoad = false;
      this.isFetchingAccounts = false;
    }, 500),
    // eslint-disable-next-line no-unused-vars
    emitUpdate(accounts, eventItem) {
      this.checkedAccounts = accounts;
      this.$emit('account-selected', accounts);
    },
    isRowCheckable(row) {
      const existsInList = this.checkedAccounts.find(r => r.id == row.id) != null;
      if (existsInList) return true;
      return this.checkedAccounts.length < 1;
    },
    deselectAccount(account) {
      // search for checked row
      const foundAccountIndex = this.checkedAccounts.findIndex(cr => cr.id == account.id);

      // if the item is found, remove it from the checked array
      if (foundAccountIndex != -1) { 
        this.checkedAccounts.splice(foundAccountIndex, 1);
        this.$emit('account-selected', this.checkedAccounts);
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.kpt-accounts-selector {
  ::v-deep .table-wrapper {
    overflow: hidden;
  }
}
</style>
